import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const UPLOAD_BASE = 'https://www.googleapis.com/upload/drive/v3/files';
const NOT_CONNECTED_MSG = 'Connect Google Drive in Settings → Connected Clouds to attach files.';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export const getDriveSession = async () => {
  try {
    const { data } = await axios.post(`${API}/cloud/access-token/google`, {}, { headers: authHeaders() });
    return data;
  } catch (e) {
    if (e.response?.status === 409) throw new Error(NOT_CONNECTED_MSG);
    throw new Error(e.response?.data?.detail || 'Could not reach Google Drive');
  }
};

const putWithProgress = (url, file, onProgress) => new Promise((resolve, reject) => {
  const xhr = new XMLHttpRequest();
  xhr.open('PUT', url);
  xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
  xhr.upload.onprogress = (e) => e.lengthComputable && onProgress && onProgress(e.loaded / e.total);
  xhr.onload = () => (xhr.status < 300 ? resolve(JSON.parse(xhr.responseText)) : reject(new Error(`Google Drive upload failed (${xhr.status})`)));
  xhr.onerror = () => reject(new Error('Network error while uploading to Google Drive'));
  xhr.send(file);
});

const multipartUpload = async (session, file, meta) => {
  const boundary = `edotask${Date.now()}`;
  const body = new Blob([
    `--${boundary}\r\nContent-Type: application/json; charset=UTF-8\r\n\r\n${JSON.stringify(meta)}\r\n`,
    `--${boundary}\r\nContent-Type: ${file.type || 'application/octet-stream'}\r\n\r\n`,
    file,
    `\r\n--${boundary}--`,
  ]);
  const res = await fetch(`${UPLOAD_BASE}?uploadType=multipart&fields=id`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': `multipart/related; boundary=${boundary}` },
    body,
  });
  if (!res.ok) throw new Error(`Google Drive upload failed (${res.status})`);
  return res.json();
};

const uploadToDrive = async (session, file, onProgress) => {
  if (!session.folder_id) {
    throw new Error('Edotask folder is not ready in your Drive yet. Please disconnect and reconnect Google Drive in Settings → Connected Clouds.');
  }
  const meta = { name: file.name, parents: [session.folder_id], ...(file.type ? { mimeType: file.type } : {}) };
  const init = await fetch(`${UPLOAD_BASE}?uploadType=resumable&fields=id`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      'Content-Type': 'application/json; charset=UTF-8',
      'X-Upload-Content-Type': file.type || 'application/octet-stream',
    },
    body: JSON.stringify(meta),
  });
  if (!init.ok) throw new Error(`Google Drive refused the upload (${init.status})`);
  const location = init.headers.get('Location');
  return location ? putWithProgress(location, file, onProgress) : multipartUpload(session, file, meta);
};

// Uploads files straight from the browser to the user's Drive, then links them to the task.
export const uploadTaskFilesToDrive = async (taskId, files, { fileType, onFileStart } = {}) => {
  const errors = [];
  if (!files?.length) return errors;
  let session;
  try {
    session = await getDriveSession();
  } catch (e) {
    return files.map((f) => ({ filename: f.name, message: e.message }));
  }
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    if (onFileStart) onFileStart(i, file);
    try {
      const driveFile = await uploadToDrive(session, file);
      await axios.post(`${API}/tasks/${taskId}/drive-attachments`, { file_id: driveFile.id, file_type: fileType }, { headers: authHeaders() });
    } catch (e) {
      errors.push({ filename: file.name, message: e.response?.data?.detail || e.message || 'Upload failed' });
    }
  }
  return errors;
};

export const formatUploadErrors = (errors) => errors.map((e) => `${e.filename}: ${e.message}`).join('\n');
